import { tryCatch } from "@bcmi-labs/art-toolkit/utils"
import { createFileRoute, notFound, redirect } from "@tanstack/react-router"

import { $rqIotApiClient } from "@/api/iot"
import { getXOrganizationHeaders } from "@/api/utils"
import { routeGuard } from "@/lib/routing/beforeLoad"
import { recentsStore } from "@/store/recents"

export const Route = createFileRoute("/_authlayout/_header/triggers/$id")({
  staticData: {
    title: "Trigger Details",
    onSpaceChange: () => redirect({ to: "/triggers", throw: true })
  },

  beforeLoad: async ({ context, params, cause }) => {
    await routeGuard(context)

    const space = context.spaceStorage.getSpace()

    // Redirects to the Thing page if `thingId` still resolves to a real Thing; otherwise falls
    // through so the trigger renders on this page instead (e.g. its Thing was later deleted).
    const redirectToThingIfExists = async (thingId: string) => {
      const { error: thingError } = await tryCatch(() =>
        context.queryClient.query({
          ...$rqIotApiClient.queryOptions("get", "/iot/v2/things/{id}", {
            params: { header: getXOrganizationHeaders(space), path: { id: thingId } }
          }),
          staleTime: "static"
        })
      )

      if (!thingError) {
        throw redirect({
          to: "/things/$id",
          params: { id: thingId },
          search: { tab: "triggers", triggerId: params.id }
        })
      }
    }

    const { data: trigger, error } = await tryCatch(() =>
      context.queryClient.query({
        ...$rqIotApiClient.queryOptions("get", "/iot/v1/triggers/{id}", {
          params: { header: getXOrganizationHeaders(space), path: { id: params.id } }
        }),
        staleTime: "static"
      })
    )

    if (error) {
      // Try purging recents in case the trigger was deleted
      recentsStore.getState().removeRecent(params.id)

      // Deleted under us -> back to the list. Otherwise a bad id -> not found.
      if (cause === "stay") throw redirect({ to: "/triggers" })
      throw notFound()
    }

    // Cloud-variable triggers only make sense in the context of their Thing (the source picker
    // there is scoped to a single Thing's variables), so they're always edited from the Thing page.
    const propertyThingId = trigger.linked_property?.property.thing_id
    if (propertyThingId) {
      await redirectToThingIfExists(propertyThingId)
    }

    // Same for a device-status trigger that targets a single device which itself is attached to a
    // Thing — it's edited from that Thing's page too, mirroring the embedded "single device" view.
    const deviceSource = trigger.device_status_source
    const singleDevice =
      deviceSource?.criteria === "INCLUDE" && deviceSource.linked_devices?.length === 1
        ? deviceSource.linked_devices[0]?.device
        : undefined
    if (singleDevice?.thing?.id) {
      await redirectToThingIfExists(singleDevice.thing.id)
    }

    return {
      trigger
    }
  },
  loader: ({ context }) => ({ trigger: context.trigger }),
  head: ctx => ({
    meta: [
      {
        title: `${ctx.loaderData?.trigger.name || "Trigger"} | Arduino Cloud`
      }
    ]
  })
})
